import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const STATES = [["BE", "Berlin"], ["BB", "Brandenburg"], ["NW", "Nordrhein-Westfalen"], ["BY", "Bayern"], ["HH", "Hamburg"], ["HE", "Hessen"], ["SN", "Sachsen"], ["BW", "Baden-Württemberg"], ["NI", "Niedersachsen"]];
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

export default function PlannerSettingsDialog({ open, onOpenChange, coachId, onSaved }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/planner/settings", { params: coachId ? { coach_id: coachId } : {} }).then((r) => setS(r.data)).catch(err);
  }, [open, coachId]);
  if (!s) return null;
  const w = s.work, rl = s.rules;
  const setW = (k, v) => setS((x) => ({ ...x, work: { ...x.work, [k]: v } }));
  const setR = (k, v) => setS((x) => ({ ...x, rules: { ...x.rules, [k]: v } }));
  const save = async () => {
    try {
      await api.put("/planner/settings/work", { weekdays: w.weekdays, start: w.start, end: w.end, state: w.state, blocks: w.blocks || [] }, { params: coachId ? { coach_id: coachId } : {} });
      if (s.can_edit_rules) {
        await api.put("/planner/settings/rules", { ...rl, min_per_week: +rl.min_per_week, ue_per_session: +rl.ue_per_session, ue_per_short_session: +rl.ue_per_short_session, max_ue_per_session_tight: +rl.max_ue_per_session_tight || 6, max_per_week_tight: +rl.max_per_week_tight || 4, duration_min: +rl.duration_min, ue_minutes: +rl.ue_minutes, reservation_hours: +rl.reservation_hours, plan_weeks: +rl.plan_weeks || 8, allow_same_day: !!rl.allow_same_day });
      }
      toast.success("Einstellungen gespeichert");
      onOpenChange(false); onSaved?.();
    } catch (e) { err(e); }
  };
  const num = (k, label) => (
    <label className="text-xs text-slate-600">{label}
      <input type="number" value={rl[k]} onChange={(e) => setR(k, e.target.value)} disabled={!s.can_edit_rules} className="input-tech mt-1" data-testid={`planner-rule-${k}`} />
    </label>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="planner-settings-dialog">
        <DialogHeader><DialogTitle>Planungs-Einstellungen</DialogTitle></DialogHeader>
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Arbeitszeit des Coaches</div>
        <div className="flex gap-1.5">
          {DAYS.map((d, i) => (
            <button key={d} type="button" data-testid={`planner-day-${i}`}
              onClick={() => setW("weekdays", w.weekdays.includes(i) ? w.weekdays.filter((x) => x !== i) : [...w.weekdays, i].sort())}
              className={`w-10 h-9 text-xs border ${w.weekdays.includes(i) ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"}`}>{d}</button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="text-xs text-slate-600">Beginn<input type="time" value={w.start} onChange={(e) => setW("start", e.target.value)} className="input-tech mt-1" data-testid="planner-work-start" /></label>
          <label className="text-xs text-slate-600">Ende<input type="time" value={w.end} onChange={(e) => setW("end", e.target.value)} className="input-tech mt-1" data-testid="planner-work-end" /></label>
          <label className="text-xs text-slate-600">Feiertage
            <select value={w.state} onChange={(e) => setW("state", e.target.value)} className="input-tech mt-1" data-testid="planner-work-state">
              {STATES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mt-3">Planungsregeln {!s.can_edit_rules && "(nur Admin)"}</div>
        <p className="text-[11px] text-slate-500">Terminanzahl = Modul-UE ÷ UE je Termin + Onboarding-/Offboarding-UE ÷ UE je Kurztermin. Verteilung gleichmäßig bis zur Frist, möglichst nicht an aufeinanderfolgenden Tagen.</p>
        <p className="text-[11px] text-slate-500">Wird die Zeit knapp, verdichtet das Portal automatisch: erst mehr UE je Termin (bis „UE je Termin (eng)"), dann mehr Termine pro Woche (bis „Termine/Woche (eng)"). Zwei Termine am selben Tag beim selben Teilnehmer werden nur geplant, wenn das unten ausdrücklich erlaubt ist.</p>
        <div className="grid grid-cols-3 gap-3">
          {num("ue_per_session", "UE je Termin")}{num("duration_min", "Dauer je Termin (Min.)")}{num("ue_per_short_session", "UE je On-/Offboarding")}
          {num("min_per_week", "Mindestens Termine/Woche")}{num("ue_minutes", "Minuten je UE")}{num("reservation_hours", "Erinnerung nach (Std.)")}
          {num("max_ue_per_session_tight", "UE je Termin (eng)")}{num("max_per_week_tight", "Termine/Woche (eng)")}
        </div>
        <label className="flex items-center gap-2 text-sm mt-2">
          <input type="checkbox" checked={!!rl.allow_same_day} onChange={(e) => setR("allow_same_day", e.target.checked)} disabled={!s.can_edit_rules} data-testid="planner-rule-allow-same-day" />
          Zwei Termine am selben Tag erlauben (nur im Notfall, wenn die Frist sonst nicht zu halten ist)
        </label>
        <label className="flex items-center gap-2 text-sm mt-2">
          <input type="checkbox" checked={!!rl.test_mode} onChange={(e) => setR("test_mode", e.target.checked)} disabled={!s.can_edit_rules} data-testid="planner-rule-test-mode" />
          Testmodus – keine echten E-Mails, keine Google-Kalender-Einträge
        </label>
        <button onClick={save} className="mt-3 w-full py-3 bg-slate-900 text-white text-sm hover:bg-black" data-testid="planner-settings-save">Speichern</button>
      </DialogContent>
    </Dialog>
  );
}
